import Typography from "@mui/material/Typography";
import ToolbarButton from "../ToolbarButton";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from "@mui/material/Menu";
import { MouseEvent, ReactNode, useState } from "react";

import NotesIcon from '@mui/icons-material/Notes';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import IconMenuItem from "../IconMenuItem";
import FontIcon from "../FontIcon";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingTagType } from "@lexical/rich-text";
import { BlockType, useToolbarContext } from "../../ToolbarContext";
import { formatBulletList, formatCheckList, formatHeading, formatNumberedList, formatParagraph, formatQuote } from "../../utils";

type BlockTypeMap = Record<BlockType, {
    icon: ReactNode;
    label: string;
    shortcut?: string;
}>;

// interface BlockTypeDropdownProps {

// }

export function BlockTypeDropdown() {
    const [editor] = useLexicalComposerContext();
    const { blockType } = useToolbarContext();
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
    }

    const handleFormat = (newBlockType: BlockType) => {
        handleMenuClose();

        // set block type to paragraph
        if (newBlockType === 'paragraph') {
            formatParagraph(editor);
        }

        // set block type to heading
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(newBlockType) && newBlockType !== blockType) {
            formatHeading(editor, blockType, newBlockType as HeadingTagType);
        }

        // set block type to bullet list
        if (newBlockType === 'bullet') {
            formatBulletList(editor, blockType);
        }

        // set block type to numbered list
        if (newBlockType === 'number') {
            formatNumberedList(editor, blockType);
        }

        // set block type to check list
        if (newBlockType === 'check') {
            formatCheckList(editor, blockType);
        }

        // set block type to quote
        if (newBlockType === 'quote') {
            formatQuote(editor, blockType);
        }
    };

    const blockTypeMap: BlockTypeMap = {
        paragraph: {
            icon: <NotesIcon />,
            label: "Normal",
            shortcut: undefined
        },
        h1: {
            icon: <FontIcon>H1</FontIcon>,
            label: "Heading 1",
            shortcut: undefined
        },
        h2: {
            icon: <FontIcon>H2</FontIcon>,
            label: "Heading 2",
            shortcut: undefined
        },
        h3: {
            icon: <FontIcon>H3</FontIcon>,
            label: "Heading 3",
            shortcut: undefined
        },
        h4: {
            icon: <FontIcon>H4</FontIcon>,
            label: "Heading 4",
            shortcut: undefined
        },
        h5: {
            icon: <FontIcon>H5</FontIcon>,
            label: "Heading 5",
            shortcut: undefined
        },
        h6: {
            icon: <FontIcon>H6</FontIcon>,
            label: "Heading 6",
            shortcut: undefined
        },
        bullet: {
            icon: <FormatListBulletedIcon />,
            label: "Bullet List",
            shortcut: undefined
        },
        number: {
            icon: <FormatListNumberedIcon />,
            label: "Numbered List",
            shortcut: undefined
        },
        check: {
            icon: <ChecklistIcon />,
            label: "Check List",
            shortcut: undefined
        },
        quote: {
            icon: <FormatQuoteIcon />,
            label: "Quote",
            shortcut: undefined
        },
    };

    return (
        <>
            {/* toolbar button to open menu for menu only actions */}
            <ToolbarButton value="" onClick={handleMenuOpen}>
                {blockTypeMap[blockType].icon}
                <Typography
                    fontSize={18}
                    lineHeight={1}
                    pl={1}
                >
                    {blockTypeMap[blockType].label}
                </Typography>
                <ArrowDropDownIcon />
            </ToolbarButton>


            {/* menu for menu only actions */}
            <Menu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={handleMenuClose}
            >
                {Object.entries(blockTypeMap).map(([key, { label, icon, shortcut }]) => (
                    <IconMenuItem
                        key={key}
                        label={label}
                        icon={icon}
                        shortcut={shortcut}
                        onClick={() => handleFormat(key as BlockType)}
                        selected={blockType === key}
                    />
                ))}
            </Menu>
        </>
    );
}

export default BlockTypeDropdown;